import type { FilterType } from '@hotjar/global-filters';
import { FilterKindEnum } from '@hotjar/global-filters';
import type { UserAttributeFilter } from '@hotjar/global-filters/src/domains/global-filters/model/FilterChain';
import { UserAttributeKind } from '@hotjar/webapp/domains/user-attributes/model';
import {
  delimit,
  delimitWithoutEncode,
  encode,
  encodeDate,
} from '../../../../../common/Responses/Filters/Query/queryUtils';
import {
  IS_GREATER_THAN,
  IS_LESS_THAN,
  IS_EXACTLY,
  IS_NOT,
  IS_BETWEEN,
  AND,
  IS_UNKNOWN,
  HAS_ANY_VALUE,
  CONTAINS,
  NOT_CONTAINS,
  STARTS_WITH,
  NOT_STARTS_WITH,
  ENDS_WITH,
  NOT_ENDS_WITH,
  IS,
  IS_NOT_EXACTLY,
  REGEX,
  IS_TRUE,
  IS_FALSE,
  IS_EXACTLY_DAYS_AGO,
  IS_MORE_THAN_DAYS_AGO,
  IS_LESS_THAN_DAYS_AGO,
  IS_ON,
  IS_AFTER,
  IS_BEFORE,
} from '../../../../../common/Responses/Filters/Query/GlobalFilterQuery/constants';

function encodeUserAttributeKind(kind: UserAttributeKind) {
  switch (kind) {
    case UserAttributeKind.Number:
      return 'number';
    case UserAttributeKind.String:
      return 'string';
    case UserAttributeKind.Email:
      return 'email';
    case UserAttributeKind.Boolean:
      return 'boolean';
    case UserAttributeKind.Date:
      return 'date';
    case UserAttributeKind.Unknown:
      return 'unknown';
    default:
      throw new Error('Unknown user attribute kind');
  }
}

function encodeUserAttributeValue(filter: FilterType) {
  if (
    filter.kind !== FilterKindEnum.UserAttribute ||
    filter.value === undefined ||
    filter.value.value === undefined
  )
    return undefined;

  switch (filter.value.kind) {
    case UserAttributeKind.Number:
      switch (filter.value.value.operation) {
        case 'IS_GREATER_THAN':
          return delimit(IS_GREATER_THAN, filter.value.value.value.toString());
        case 'IS_LESS_THAN':
          return delimit(IS_LESS_THAN, filter.value.value.value.toString());
        case 'IS_EXACTLY':
          return delimit(IS_EXACTLY, filter.value.value.value.toString());
        case 'IS_NOT':
          return delimit(IS_NOT, filter.value.value.value.toString());
        case 'IS_BETWEEN':
          return delimit(
            IS_BETWEEN,
            filter.value.value.from.toString(),
            AND,
            filter.value.value.to.toString(),
          );
        case 'IS_UNKNOWN':
          return IS_UNKNOWN;
        case 'HAS_ANY_VALUE':
          return HAS_ANY_VALUE;
      }
    case UserAttributeKind.String:
    case UserAttributeKind.Email:
      switch (filter.value.value.operation) {
        case 'CONTAINS':
          return delimit(CONTAINS, filter.value.value.value);
        case 'NOT_CONTAINS':
          return delimit(NOT_CONTAINS, filter.value.value.value);
        case 'STARTS_WITH':
          return delimit(STARTS_WITH, filter.value.value.value);
        case 'NOT_STARTS_WITH':
          return delimit(NOT_STARTS_WITH, filter.value.value.value);
        case 'ENDS_WITH':
          return delimit(ENDS_WITH, filter.value.value.value);
        case 'NOT_ENDS_WITH':
          return delimit(NOT_ENDS_WITH, filter.value.value.value);
        case 'IS':
          return delimit(IS, filter.value.value.value);
        case 'IS_NOT':
          return delimit(IS_NOT, filter.value.value.value.toString());
        case 'IS_EXACTLY':
          return delimit(IS_EXACTLY, filter.value.value.value.toString());
        case 'IS_NOT_EXACTLY':
          return delimit(IS_NOT_EXACTLY, filter.value.value.value);
        case 'IS_UNKNOWN':
          return IS_UNKNOWN;
        case 'HAS_ANY_VALUE':
          return HAS_ANY_VALUE;
        case 'REGEX':
          return delimit(REGEX, filter.value.value.value);
      }
    case UserAttributeKind.Boolean:
      switch (filter.value.value.operation) {
        case 'IS_TRUE':
          return IS_TRUE;
        case 'IS_FALSE':
          return IS_FALSE;
        case 'IS_UNKNOWN':
          return IS_UNKNOWN;
        case 'HAS_ANY_VALUE':
          return HAS_ANY_VALUE;
      }
    case UserAttributeKind.Date:
      switch (filter.value.value.operation) {
        case 'IS_EXACTLY_DAYS_AGO':
          return delimit(IS_EXACTLY_DAYS_AGO, filter.value.value.value.toString());
        case 'IS_MORE_THAN_DAYS_AGO':
          return delimit(IS_MORE_THAN_DAYS_AGO, filter.value.value.value.toString());
        case 'IS_LESS_THAN_DAYS_AGO':
          return delimit(IS_LESS_THAN_DAYS_AGO, filter.value.value.value.toString());
        case 'IS_ON':
          return delimit(IS_ON, encodeDate(filter.value.value.value));
        case 'IS_AFTER':
          return delimit(IS_AFTER, encodeDate(filter.value.value.value));
        case 'IS_BEFORE':
          return delimit(IS_BEFORE, encodeDate(filter.value.value.value));
        case 'IS_UNKNOWN':
          return IS_UNKNOWN;
        case 'HAS_ANY_VALUE':
          return HAS_ANY_VALUE;
      }
    default:
      throw new Error('Unknown filter type');
  }
}

export function encodeUserAttribute(filter: UserAttributeFilter) {
  if (filter.value === undefined || filter.value.name === undefined) {
    return undefined;
  }
  const value = encodeUserAttributeValue(filter);
  if (value === undefined) return undefined;
  const kind = encodeUserAttributeKind(filter.value.kind);
  const name = encode(filter.value.name);
  return delimitWithoutEncode(name, kind, value);
}
